import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

// Use REACT_APP_BACKEND_URL if set, otherwise use current origin (for production)
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

// Available themes for clubs
export const CLUB_THEMES = {
  emerald: {
    id: 'emerald',
    name: 'Эмералд',
    primary: '#00E054',
    secondary: '#059669',
    accent: '#10B981',
    background: '#050505',
    card: 'rgba(0, 224, 84, 0.05)',
    border: 'rgba(0, 224, 84, 0.2)',
    gradient: 'from-emerald-500/20 to-green-500/10'
  },
  ocean: {
    id: 'ocean',
    name: 'Далай',
    primary: '#0EA5E9',
    secondary: '#0284C7',
    accent: '#38BDF8',
    background: '#030712',
    card: 'rgba(14, 165, 233, 0.05)',
    border: 'rgba(14, 165, 233, 0.2)',
    gradient: 'from-sky-500/20 to-blue-500/10'
  },
  sunset: {
    id: 'sunset',
    name: 'Нарны жаргалт',
    primary: '#F97316',
    secondary: '#EA580C',
    accent: '#FB923C',
    background: '#0C0A09',
    card: 'rgba(249, 115, 22, 0.05)',
    border: 'rgba(249, 115, 22, 0.2)',
    gradient: 'from-orange-500/20 to-amber-500/10'
  },
  purple: {
    id: 'purple',
    name: 'Ягаан',
    primary: '#A855F7',
    secondary: '#9333EA',
    accent: '#C084FC',
    background: '#09090B',
    card: 'rgba(168, 85, 247, 0.05)',
    border: 'rgba(168, 85, 247, 0.2)',
    gradient: 'from-purple-500/20 to-violet-500/10'
  },
  crimson: {
    id: 'crimson',
    name: 'Улаан',
    primary: '#EF4444',
    secondary: '#DC2626',
    accent: '#F87171',
    background: '#0A0A0A',
    card: 'rgba(239, 68, 68, 0.05)',
    border: 'rgba(239, 68, 68, 0.2)',
    gradient: 'from-red-500/20 to-rose-500/10'
  },
  gold: {
    id: 'gold',
    name: 'Алтан',
    primary: '#EAB308',
    secondary: '#CA8A04',
    accent: '#FACC15',
    background: '#0A0A08',
    card: 'rgba(234, 179, 8, 0.05)',
    border: 'rgba(234, 179, 8, 0.2)',
    gradient: 'from-yellow-500/20 to-amber-500/10'
  }
};

// Default club data
const DEFAULT_CLUB = {
  id: 'default',
  name: '147 CLUB',
  subdomain: 'default',
  logo_url: null,
  theme: 'emerald',
  primary_color: '#00E054'
};

// Create context
const ClubContext = createContext(null);

export const ClubProvider = ({ children }) => {
  const [club, setClub] = useState(DEFAULT_CLUB);
  const [theme, setTheme] = useState(CLUB_THEMES.emerald);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    detectClub();
  }, []);

  const detectClub = async () => {
    try {
      // Get subdomain from hostname
      const hostname = window.location.hostname;
      let subdomain = 'default';

      // Check if it's a subdomain (e.g., darkhan.clubos.mn)
      const parts = hostname.split('.');
      if (parts.length >= 3 && parts[0] !== 'www') {
        subdomain = parts[0];
      }

      // For preview/development, check URL params
      const urlParams = new URLSearchParams(window.location.search);
      const clubParam = urlParams.get('club');
      if (clubParam) {
        subdomain = clubParam;
      }

      // Fetch club info from API
      if (subdomain !== 'default') {
        try {
          const res = await axios.get(`${API}/public/club/${subdomain}`);
          if (res.data && res.data.id !== 'default') {
            const clubData = res.data;
            setClub(clubData);

            // Set theme based on club's theme or primary_color
            if (clubData.theme && CLUB_THEMES[clubData.theme]) {
              setTheme(CLUB_THEMES[clubData.theme]);
            } else if (clubData.primary_color) {
              // Create custom theme from primary_color
              setTheme({
                ...CLUB_THEMES.emerald,
                primary: clubData.primary_color,
                secondary: clubData.primary_color,
                accent: clubData.primary_color
              });
            }
          }
        } catch (error) {
          console.log('Club not found, using default');
        }
      }
    } catch (error) {
      console.error('Error detecting club:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateClubTheme = (themeId) => {
    if (CLUB_THEMES[themeId]) {
      setTheme(CLUB_THEMES[themeId]);
      setClub(prev => ({ ...prev, theme: themeId }));
    }
  };

  // Apply CSS variables for theme
  useEffect(() => {
    if (theme) {
      document.documentElement.style.setProperty('--color-primary', theme.primary);
      document.documentElement.style.setProperty('--color-secondary', theme.secondary);
      document.documentElement.style.setProperty('--color-accent', theme.accent);
      document.documentElement.style.setProperty('--color-background', theme.background);
      document.documentElement.style.setProperty('--color-card', theme.card);
      document.documentElement.style.setProperty('--color-border', theme.border);
    }
  }, [theme]);

  return (
    <ClubContext.Provider value={{
      club,
      theme,
      loading,
      updateClubTheme,
      themes: CLUB_THEMES,
      isMultiTenant: club.id !== 'default'
    }}>
      {children}
    </ClubContext.Provider>
  );
};

export const useClub = () => {
  const context = useContext(ClubContext);
  if (!context) {
    throw new Error('useClub must be used within a ClubProvider');
  }
  return context;
};

export default ClubContext;
